/**
 * Ross → Iris "Price Reverted" card
 *
 * Informational: once inventory recovered and velocity dropped, Ross (the
 * analysis agent) triggered a revert of an automatic Iris price increase and
 * Iris (the executing agent) put the price back — or tried to. Same dual-agent
 * treatment as Iris → Dave deals: the executing agent leads the card, the
 * triggering agent is credited via a badge and "Triggered By". There is
 * nothing to accept or reject once it succeeded: those reverts are listed in
 * the "Done for you" section of MultiDecisionAccordion. This card renders the
 * all_attempts_failed case, where the seller accepts (or rejects) the revert.
 *
 * Only decision.target_price and selling_price are converted to USD by the
 * backend, so decision.previous_price and features.* are never shown as money.
 */

import React from "react";

import { AGENT_INFO } from "../../constants";

import RuleEngineDecision from "./RuleEngineDecision";

const formatUsd = (value) =>
  value == null || Number.isNaN(Number(value))
    ? null
    : `$${Number(value).toFixed(2)}`;

const ROSS_REVERT_TRIGGER_REASON = "Inventory recovered";

export const buildRossRevertDoneMessage = (rossData) => {
  const to = formatUsd(rossData?.decision?.target_price);
  return `${AGENT_INFO.iris.name} reverted price${to ? ` to ${to}` : ""} from ${
    AGENT_INFO.ross.name
  } suggestion (${ROSS_REVERT_TRIGGER_REASON})`;
};

// Secondary detail lines: why Ross reverted, and the prices involved
export const getRossRevertDetailLines = (rossData) => {
  const decision = rossData?.decision || {};
  const features = rossData?.features || {};
  const lines = [];

  if (decision.reasoning) {
    lines.push(decision.reasoning);
  } else if (
    features.pre_units_per_day != null &&
    features.post_units_per_day != null
  ) {
    const dos =
      features.days_of_supply != null
        ? `, ${Math.round(features.days_of_supply)} days of supply`
        : "";
    lines.push(
      `Units/day ${features.pre_units_per_day} → ${features.post_units_per_day} after the increase${dos}.`
    );
  }

  const from = formatUsd(rossData?.selling_price);
  const to = formatUsd(decision.target_price);
  if (from && to && rossData?.automated_execution_status === "succeeded") {
    lines.push(`Reverted from ${from} → ${to}.`);
  }

  const live = formatUsd(rossData?.current_updated_price);
  if (live) lines.push(`Current live price: ${live}.`);

  return lines;
};

export const SELLER_CENTRAL_OVERRIDE_NOTE =
  "You can override this price in Seller Central.";

// The failed case is an Iris price change the seller still has to make, so it
// reuses Iris's failed-automation card (Accept/Reject/Why?) with Ross credited
// as the trigger. Accept sets decision.target_price, the pre-increase price.
const RossRevertCard = ({
  rossData,
  asin,
  sku,
  store,
  marketplace,
  currentPrice,
  onRemove,
}) => {
  const to = formatUsd(rossData?.decision?.target_price);
  return (
    <RuleEngineDecision
      asin={asin}
      sku={sku}
      store={store}
      marketplace={marketplace}
      executionId={rossData.execution_id}
      targetPrice={rossData.decision?.target_price || null}
      sellingPrice={rossData.selling_price || null}
      action="Revert Price"
      agentName="iris"
      currentPrice={currentPrice}
      decisionType="price"
      automatedExecutionStatus={rossData.automated_execution_status}
      onRemove={onRemove}
      triggeredBy={{
        agent: AGENT_INFO.ross,
        reason: ROSS_REVERT_TRIGGER_REASON,
      }}
      failedMessage={
        to
          ? `We couldn't revert the price automatically. Accept to set it back to ${to}, or reject to keep the current price.`
          : "We couldn't revert the price automatically. Please review the suggestion and accept or reject it."
      }
      reasonLines={getRossRevertDetailLines(rossData)}
    />
  );
};

export default RossRevertCard;
